import { useEffect } from "react";
import { SITE_URL, SITE_NAME, DEFAULT_DESC } from "@/lib/site";

// Imperatively manage <head> tags so there is exactly ONE of each
// (title, description, canonical, og/twitter). This avoids duplicate tags
// even when a page renders <Seo> more than once, and keeps the static
// index.html defaults as the pre-JS fallback (updated in place here).
function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function Seo({ title, description = DEFAULT_DESC, path = "", image, type = "website", jsonLd, noindex = false, geo }) {
  const url = SITE_URL + path;
  const img = image || `${SITE_URL}/in-home-sales.jpg`;
  const full = !title
    ? `${SITE_NAME}, Hire Sales Reps & Find Sales Jobs`
    : title.includes(SITE_NAME)
      ? title
      : `${title} | ${SITE_NAME}`;
  const jsonKey = jsonLd ? JSON.stringify(jsonLd) : "";
  const geoKey = geo ? JSON.stringify(geo) : "";

  useEffect(() => {
    document.title = full;
    upsertMeta("name", "description", description);
    upsertLink("canonical", url);

    const robots = document.head.querySelector('meta[name="robots"]');
    if (noindex) {
      if (robots) robots.setAttribute("content", "noindex, nofollow");
      else upsertMeta("name", "robots", "noindex, nofollow");
    } else if (robots) {
      robots.remove();
    }

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", full);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", full);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // Geo meta tags (region/placename/position/ICBM) for local SEO, removed when not provided.
    const geoKeys = [["name", "geo.region"], ["name", "geo.placename"], ["name", "geo.position"], ["name", "ICBM"]];
    if (geo && geo.lat != null && geo.lon != null) {
      upsertMeta("name", "geo.region", geo.region ? `US-${geo.region}` : "US");
      upsertMeta("name", "geo.placename", geo.placename || "");
      upsertMeta("name", "geo.position", `${geo.lat};${geo.lon}`);
      upsertMeta("name", "ICBM", `${geo.lat}, ${geo.lon}`);
    } else {
      geoKeys.forEach(([a, k]) => { const el = document.head.querySelector(`meta[${a}="${k}"]`); if (el) el.remove(); });
    }

    document.head.querySelectorAll("script[data-seo-jsonld]").forEach((s) => s.remove());
    const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
    blocks.forEach((b) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "1");
      s.textContent = JSON.stringify(b);
      document.head.appendChild(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [full, description, url, img, type, noindex, jsonKey, geoKey]);

  return null;
}

export default Seo;
